import { observe, observable, action, Lambda, IObjectDidChange } from "mobx";
import { SuperBannerItem, DataFacade, PickedEvent, MasterEvent, Market, Selection, SelectionType, ItemState } from "../data";
import { EventPushResponse, entities,/*TODO, Dictionary*/ } from "sbtech-sports-data-api-client";
import { MasterEventParser, FormattedDateTime } from "./MasterEventParser";
import { MarketGetter, SelectionParser } from ".";
import { globalTimeTicker, GlobalTimeTicker, eBranches } from "sb-resp-lib";
import { LoginControlHelper } from "./helpers/LoginControlHelper";
import { MarketParser } from "./MarketParser";

export type UIPreferences = {
    oddStyle: string,
    timeZone: number,
    isSwapped: boolean
};

// typed params object needed for addOddToSlip method of BetSlipUtil

export type AddOddDataType = {
    masterEventID: number;
    eventID: number,
    lineID: number,
    odds: number | string,
    lineGroupID: number,
    lineTypeID: number,
    rowTypeID: number,
    points: number,
    isLive: boolean,
    isQA: boolean,
    eventTypeId: number,
    leagueId: number,
    betId: number,
    preventUpdating: boolean,
    isFavedTeam: boolean
};

export enum eventTypeForGameBlock {
    Prelive = 0,
    Live = 1,
    Outright = 99
}

export enum MainMarketsMapping {
    ML = 1,
    HC = 2,
    OU = 3
}

// typed params object needed for OpenGame method of navHandle

export type OpenGameDataType = {
    event: any,
    id: number,
    isLive: boolean,
    evTypeForGameBlock: number,
    shouldHideSidePanels: boolean,
    branchID: number;
    regionID: number;
};

export type Action<T> = (p: T) => void;

/*
    The class has 'istanbul ignore' flag , because of testing typescript interface with Jest.
    It`s known issue : (https://github.com/kulshekhar/ts-jest/issues/378)
    The class is fully tested, but propery "events" implement interface "EventsObject" which is not
    testable with Jest - there is no types like in typescript
 */

/* istanbul ignore next */
export class DataParser {
    disposeDataCollection: Lambda;

    disposeShouldRemoveEvents: Lambda;
    disposeTimeZone: Lambda;
    disposeOddStyleChange: Lambda;
    disposeCurrentSelectedSportIdChange: Lambda;
    eventTimeTickingDisposer: Lambda;
    dataFacade: DataFacade;
    betSlipUtil: BetSlipUtil;
    navHandler: Application;
    marketGetter: MarketGetter;

    @observable currentSelectedSportId: number;
    @observable items: SuperBannerItem[];

    allItems: SuperBannerItem[];

    constructor(dataFacade: DataFacade) {
        this.dataFacade = dataFacade;
        this.allItems = dataFacade.cms.SuperBannerItems;
        this.betSlipUtil = dataFacade.betSlipUtil;
        this.navHandler = dataFacade.navHandler;
        this.items = [];
        this.currentSelectedSportId = dataFacade.currentSelectedSportId;
        this.marketGetter = new MarketGetter();

        this.updateItemsForDevice();
        this.updateItemState(false);

        this.disposeDataCollection = observe(this.dataFacade, "dataCollection", (change) => {
            this.updateCollection(change.newValue);
        });

        this.disposeShouldRemoveEvents = observe(this.dataFacade, "shouldRemoveEvents", (change) => {
            if (change.newValue === true) {
                this.removeAllEvents();
                this.updateItemsState();
            }
        });

        this.disposeTimeZone = observe(this.dataFacade, "currentTimeZone", (change) => {
            this.updateEventsDate(change.newValue);
        });

        this.disposeOddStyleChange = observe(this.dataFacade, "currentOddStyle", (change) => {
            this.updateEventsOddStyle(change.newValue);
        });

        this.disposeOddStyleChange = observe(this.dataFacade, "isUserLoggedIn", (change) => {
            this.updateItemState(change.newValue);
        });

        this.disposeCurrentSelectedSportIdChange = observe(this.dataFacade, "currentSelectedSportId", (change) => {
            this.updateCurrentSelectedSportId(change.newValue);
        });

        this.eventTimeTickingDisposer = observe(globalTimeTicker, (change: IObjectDidChange) => {
            let changedObject: GlobalTimeTicker = change.object as GlobalTimeTicker;
            this.updateTimeElapsed(changedObject.interval);
        });

        this.dataFacade.betSlipUtil.onAdded()[this.dataFacade.blockUniqueId] = this.selectSelection.bind(this);
        this.dataFacade.betSlipUtil.onRemoved()[this.dataFacade.blockUniqueId] = this.unselectSelection.bind(this);
    }

    activate(): void {
        this.dataFacade.activate();

        this.dataFacade.betSlipUtil.onAdded()[this.dataFacade.blockUniqueId] = this.selectSelection.bind(this);
        this.dataFacade.betSlipUtil.onRemoved()[this.dataFacade.blockUniqueId] = this.unselectSelection.bind(this);
    }

    deactive(): void {
        this.disposeTimeZone();
        this.disposeOddStyleChange();
        this.disposeDataCollection();
        this.disposeCurrentSelectedSportIdChange();

        delete this.dataFacade.betSlipUtil.onAdded()[this.dataFacade.blockUniqueId];
        delete this.dataFacade.betSlipUtil.onRemoved()[this.dataFacade.blockUniqueId];
    }

    updateCollection(dataCollection: EventPushResponse | undefined): void {
        if (dataCollection !== undefined) {
            this.removeEvents(dataCollection.data.remove.events);
            this.removeMarkets(dataCollection.data.remove.markets);

            this.upsertEvents(dataCollection.data.add.events);
            this.upsertMarkets(dataCollection.data.add.markets);

            this.upsertEvents(dataCollection.data.change.events);
            this.upsertMarkets(dataCollection.data.change.markets);

            this.updateItemsState();
            this.updateBetslipRelatedData();
        }
    }

    removeAllEvents(): void {
        this.iterateEvents(pickedEvent => {
            pickedEvent.deleteEvent();
            pickedEvent.updateMarket(this.marketGetter.getCurrentMarket(pickedEvent));
        });
    }

    removeEvents(entries: string[]): void {
        this.iterate(entries, id => {
            this.findEvent(id, pickedEvent => {
                pickedEvent.deleteEvent();
                pickedEvent.updateMarket(this.marketGetter.getCurrentMarket(pickedEvent));
            });
        });
    }

    removeMarkets(marketIds: string[]): void {
        if (marketIds.length > 0) {
            this.iterateEvents(pickedEvent => {
                pickedEvent.removeMarkets(marketIds);
                pickedEvent.updateMarket(this.marketGetter.getCurrentMarket(pickedEvent));
            });
        }
    }

    upsertEvents(sportEvents: entities.SportEvent[]): void {
        this.iterate(sportEvents, sportEvent => {
            this.findEvent(sportEvent.id, pickedEvent => {
                try {
                    let parsedMasterEvent: MasterEvent =
                        MasterEventParser.instance.parse(sportEvent, {
                            oddStyle: this.dataFacade.currentOddStyle,
                            timeZone: this.dataFacade.currentTimeZone,
                            isSwapped: sportEvent.isTeamSwap
                        });

                    pickedEvent.upsertEvent(parsedMasterEvent);

                    if (pickedEvent.event) {
                        this.updateFormattedDate(pickedEvent.event);
                        pickedEvent.updateMarket(this.marketGetter.getCurrentMarket(pickedEvent));
                    }
                } catch (ex) {
                    //console["log"](ex)
                }
            });
        });
    }

    upsertMarkets(markets: entities.Market[]): void {
        this.iterate(markets, market => {
            this.findEvent(market.eventId, pickedEvent => {
                try {
                    if (pickedEvent.event) {
                        let existingMarket = this.existingMarket(pickedEvent.event, market);
                        let parsedMarket: Market = MarketParser.instance.parse(market, existingMarket, {
                            oddStyle: this.dataFacade.currentOddStyle,
                            timeZone: this.dataFacade.currentTimeZone,
                            isSwapped: pickedEvent.event.isTeamSwapEnabled
                        });
                        pickedEvent.upsertMarket(parsedMarket);
                        this.updateFormattedDate(pickedEvent.event);
                        pickedEvent.updateMarket(this.marketGetter.getCurrentMarket(pickedEvent));
                    }
                } catch (ex) {
                    //console["log"](ex)
                }
            });
        });
    }

    existingMarket(masterEvent: MasterEvent, market: entities.Market): Market | undefined {
        return masterEvent.markets.find(m => m.id === market.id);
    }

    private updateItemsState(): void {
        this.iterateEvents(pickedEvent => {
            if ((pickedEvent.MasterEventID || pickedEvent.EventID) && (!pickedEvent.event || !pickedEvent.market)) {
                pickedEvent.setState(ItemState.unavailable);
                return;
            }
            pickedEvent.setState(ItemState.available);
        });
    }

    private updateBetslipRelatedData(): void {
        this.iterate(this.items, item => {
            if (item.PickedEvent && item.PickedEvent.market && item.PickedEvent.event) {
                let event: MasterEvent = item.PickedEvent.event;
                let market: Market = item.PickedEvent.market;

                this.iterate(item.PickedEvent.market.selections,
                    selection => {
                        selection.updateIsSelected(this.isSelectionInBetSlip(event, market, selection));
                    });
            }
        });
    }

    updateEventsDate(newTimeZone: number): void {
        this.iterateEvents(pickedEvent => {
            if (!pickedEvent.event) {
                return;
            }

            let masterEvent: MasterEvent = pickedEvent.event as MasterEvent;
            this.updateFormattedDate(masterEvent, newTimeZone);
        });
    }

    updateEventsOddStyle(newOddStyle: string): void {
        this.iterateEvents(pickedEvent => {
            if (!pickedEvent.event) {
                return;
            }

            let masterEvent: MasterEvent = pickedEvent.event as MasterEvent;
            this.iterate(masterEvent.markets, market => {
                this.iterate(market.selections, selection => {
                    selection.updateOdds(SelectionParser.instance.getOddsForOddStyle(selection, newOddStyle));
                });
            });
        });
    }

    updateFormattedDate(masterEvent: MasterEvent, newTimeZone?: number): void {
        let timezone: number = typeof newTimeZone !== "undefined" ? newTimeZone : this.dataFacade.currentTimeZone;
        let dateTime: FormattedDateTime = MasterEventParser.instance.getFormattedDateTime(masterEvent.eventDateTime, timezone);

        masterEvent.updateDateTime(dateTime.Date, dateTime.Time);
    }

    updateTimeElapsed(interval: number): void {
        this.iterateEvents(pickedEvent => {
            if (pickedEvent.event && pickedEvent.event.liveGameState) {
                var gameTime: number = MasterEventParser.instance.getLiveGameStateTime(pickedEvent.event.liveGameState, interval);
                pickedEvent.event.liveGameState.updateGameTime(gameTime);

                var countdownTime: number = MasterEventParser.instance.getLiveStartCountdownTime(
                    pickedEvent.event.liveStartCountdown, interval);
                pickedEvent.event.updateLiveStartCountdown(countdownTime);
            }
        });
    }

    @action.bound
    updateItemsForDevice(): void {
        this.items = this.dataFacade.deviceDetectHelper.itemDeviceFilter(this.allItems) as SuperBannerItem[];
    }

    /* istanbul ignore next */
    @action.bound
    updateItemState(isUserLoggedIn: boolean): void {
        this.iterate(this.items, item => item.setState(LoginControlHelper.instance.validate(item, isUserLoggedIn)
            ? ItemState.available : ItemState.unavailable));
    }

    private iterate<T>(entries: T[], action: Action<T>): void {
        for (let index: number = 0; index < entries.length; index++) {
            action.call(this, entries[index]);
        }
    }

    private iterateEvents(action: Action<PickedEvent>): void {
        this.iterate(this.items,
            item => {
                if (item.PickedEvent) {
                    action.call(this, item.PickedEvent as PickedEvent);
                }
            }
        );
    }

    /* istanbul ignore next */
    private findEvent(eventId: string, action: Action<PickedEvent>): void {
        this.iterateEvents(pickedEvent => {
            if (pickedEvent.MasterEventID.toString() === eventId || pickedEvent.EventID.toString() === eventId) {
                action(pickedEvent);
            }
        });
    }

    private selectSelection(sender: any, data: any): void {
        this.updateSelectionSelectedState(data.LineID, true);
    }

    private unselectSelection(data: any): void {
        this.updateSelectionSelectedState(data.LineID, false);
    }

    /* istanbul ignore next */
    private updateSelectionSelectedState(selectionId: number, isSelected: boolean): void {
        let selection: Selection | undefined = undefined;
        this.iterateEvents((item) => {
            if (item.market) {
                selection = item.market.selections
                    .find(sel => sel.sbtechId === selectionId.toString());

                if (selection) { (selection as Selection).updateIsSelected(isSelected); }
            }
        });
    }

    /* istanbul ignore next */
    isSelectionInBetSlip(event: MasterEvent, market: Market, selection: Selection): boolean {
        let oddData: AddOddDataType = this.getAddOddDataTypeObj(event, market, selection);

        let isInBetSlip: boolean = this.betSlipUtil
            .isOddInSlip(
                oddData.masterEventID,
                oddData.eventID,
                oddData.lineID,
                oddData.lineGroupID,
                oddData.rowTypeID,
                oddData.lineTypeID,
                oddData.isLive,
                oddData.isQA
            );

        return isInBetSlip;
    }

    imageClickHandler(item: SuperBannerItem, e: React.MouseEvent<HTMLAnchorElement>): void {
        if (item.PickedEvent && item.PickedEvent.event) {
            this.openGameTranspiler(item.PickedEvent, e);
        } else if (item.Link) {
            window.location.href = item.Link;
        }
    }

    /* istanbul ignore next */
    addToSlipTranspiler(selectionId: string, eventId: string): void {
        let selection!: Selection;
        let market!: Market;
        let event!: MasterEvent;

        this.findEvent(eventId,
            _pickedEvent => {
                if (_pickedEvent.event && _pickedEvent.event.markets) {
                    this.iterate(_pickedEvent.event.markets, _market => {
                        if (_market.selections) {
                            this.iterate(_market.selections, _sel => {
                                if (_sel.id === selectionId) {
                                    selection = _sel;
                                    market = _market;
                                    event = _pickedEvent.event as MasterEvent;
                                }
                            });
                        }
                    });
                }
            }
        );

        if (!selection) {
            return;
        }

        let addOddData: AddOddDataType = this.getAddOddDataTypeObj(event, market, selection);

        this.betSlipUtil.addOddToSlip(
            addOddData.masterEventID,
            addOddData.eventID,
            addOddData.lineID,
            addOddData.odds,
            addOddData.lineGroupID,
            addOddData.lineTypeID,
            addOddData.rowTypeID,
            addOddData.points,
            addOddData.isLive,
            addOddData.isQA,
            addOddData.eventTypeId,
            addOddData.leagueId,
            addOddData.betId,
            addOddData.preventUpdating,
            addOddData.isFavedTeam
        );
    }

    /* istanbul ignore next */
    getAddOddDataTypeObj(event: MasterEvent, market: Market, selection: Selection): AddOddDataType {
        return {
            masterEventID: market.type === SelectionType.QA ? 0 : parseInt(event.id),
            eventID: market.type === SelectionType.QA
                ? parseInt(event.id)
                : parseInt(this.getMarketId(market)),
            lineID: parseInt(selection.sbtechId),
            odds: parseInt(selection.displayOdds["american"]
                ? selection.displayOdds["american"].replace("−", "-")
                : selection.displayOdds["american"]),
            lineGroupID: parseInt(selection.lineGroupId),
            lineTypeID: this.getLineTypeId(market.type),
            rowTypeID: selection.getSide(),
            points: selection.points,
            isLive: event.isLive,
            isQA: market.type === SelectionType.QA,
            eventTypeId: parseInt(this.getMarketTypeId(market)),
            leagueId: parseInt(event.leagueID),
            betId: 0,
            preventUpdating: false,
            isFavedTeam: false
        };
    }

    /* istanbul ignore next */
    openGameTranspiler(pickedEvent: PickedEvent, e: React.MouseEvent<HTMLAnchorElement>): void {
        if (!pickedEvent || !pickedEvent.event) { return; }

        let event: MasterEvent = pickedEvent.event;
        e.preventDefault();
        let mouseEvent = { ...e, target: e.currentTarget };

        let openGameData: OpenGameDataType = {
            event: mouseEvent,
            id: parseInt(event.id),
            isLive: event.isLive,
            evTypeForGameBlock: this.getEventTypeForGameBlock(pickedEvent),
            shouldHideSidePanels: false,
            branchID: parseInt(event.sportID),
            regionID: parseInt(event.regionId)
        };

        if (openGameData.branchID === eBranches.HorseRacing
            || openGameData.branchID === eBranches.GreyHoundsRacing) {
            OpenRacingGame(
                openGameData.event,
                openGameData.id,
                openGameData.regionID,
                pickedEvent.Mode ? parseInt(pickedEvent.Mode) : 0,
                openGameData.branchID,
                0, 0, 0, 0, 0, 0, 0, 0
            );

        } else {
            this.navHandler.OpenGame(
                openGameData.event,
                openGameData.id,
                openGameData.isLive,
                openGameData.evTypeForGameBlock,
                openGameData.shouldHideSidePanels,
                openGameData.branchID
            );
        }
    }

    /* istanbul ignore next */
    openRaceGame(link: string) {
        return window.location.href = link;
    }

    getEventTypeForGameBlock(pickedEvent: PickedEvent): number {
        let event: MasterEvent = pickedEvent.event as MasterEvent;

        if (pickedEvent.IsOutright) {
            return eventTypeForGameBlock.Outright;
        }
        if (event.isLive) {
            return eventTypeForGameBlock.Live;
        } else { return eventTypeForGameBlock.Prelive; }
    }

    /* istanbul ignore next */
    getMarketId(market: Market): string {
        if (this.isMainMarket(market)) {
            return market.id.substring(2, market.id.length);
        }

        return market.id;
    }

    /* istanbul ignore next */
    getMarketTypeId(market: Market): string {
        if (market.type === SelectionType.QA) {
            return market.typeId;
        }

        if (this.isMainMarket(market)) {
            return market.typeId.substring(2, market.typeId.length);
        }

        return market.typeId;
    }

    @action updateCurrentSelectedSportId(sportId: number): void {
        this.currentSelectedSportId = sportId;
    }

    private isMainMarket(market: Market): boolean {
        return market && (market.id.charAt(0) === MainMarketsMapping.ML.toString())
            || (market.id.charAt(0) === MainMarketsMapping.HC.toString())
            || (market.id.charAt(0) === MainMarketsMapping.OU.toString());
    }

    /* istanbul ignore next */
    private getLineTypeId(slectionType: SelectionType): number {
        switch (slectionType) {
            case SelectionType.QA:
                return 0;
            case SelectionType.ML:
                return 1;
            case SelectionType.HC:
                return 2;
            case SelectionType.OU:
                return 3;
        }

        return 0;
    }
}
